import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Check, X, Loader2, Users, Shield, Newspaper, RefreshCw,
  ExternalLink, UserCog, AlertTriangle, Lock, Key, Power, PowerOff,
  Calendar, BarChart3, Megaphone, Save, Plus, Trash2, BadgeCheck,
  MessageSquare, ShoppingBag, FolderOpen, Flag,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TileReport = ({ data, type }) => {
  if (!data) return null;
  const items = {
    users: [
      { label: 'New this week', value: data.new_this_week },
      { label: 'New this month', value: data.new_this_month },
      { label: 'Active', value: data.active },
      { label: 'Deactivated', value: data.deactivated },
    ],
    posts: [
      { label: 'This week', value: data.this_week },
      { label: 'This month', value: data.this_month },
    ],
    events: [
      { label: 'Total', value: data.total },
      { label: 'Upcoming', value: data.upcoming },
      { label: 'Pending Registrations', value: data.pending_registrations },
      { label: 'Approved', value: data.approved_registrations },
    ],
    services: [
      { label: 'Total services', value: data.total },
      { label: 'Total purchases', value: data.total_purchases },
      { label: 'Purchases this week', value: data.purchases_this_week },
    ],
    discussions: [
      { label: 'Total', value: data.total },
      { label: 'This week', value: data.this_week },
    ],
    groups: [{ label: 'Total', value: data.total }],
    meetings: [
      { label: 'Total', value: data.total },
      { label: 'Pending', value: data.pending },
    ],
  };
  const reportItems = items[type] || [];
  return (
    <div className="mt-3 pt-3 border-t border-border/50 grid grid-cols-2 gap-2" data-testid={`report-${type}`}>
      {reportItems.map((item, i) => (
        <div key={i} className="text-xs">
          <span className="text-muted-foreground">{item.label}:</span>{' '}
          <span className="font-semibold">{item.value ?? 0}</span>
        </div>
      ))}
    </div>
  );
};

const AdminPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const [pendingUsers, setPendingUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [pendingNews, setPendingNews] = useState([]);
  const [auditAlerts, setAuditAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newsLoading, setNewsLoading] = useState(true);
  const [fetchingNews, setFetchingNews] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [reports, setReports] = useState(null);
  const [eventRegistrations, setEventRegistrations] = useState([]);
  const [regFilter, setRegFilter] = useState('pending');
  const [regsLoading, setRegsLoading] = useState(false);
  const [facts, setFacts] = useState([]);
  const [factsLoading, setFactsLoading] = useState(true);
  const [savingFacts, setSavingFacts] = useState(false);
  const [managingUser, setManagingUser] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [forceChange, setForceChange] = useState(true);

  // Content management
  const [discussions, setDiscussions] = useState([]);
  const [groups, setGroups] = useState([]);
  const [services, setServices] = useState([]);
  const [events, setEvents] = useState([]);
  const [contentLoading, setContentLoading] = useState(false);
  const [contentTab, setContentTab] = useState('discussions');

  const authOpts = useCallback(() => ({ headers: getAuthHeaders(), withCredentials: true }), [getAuthHeaders]);

  const fetchData = useCallback(async () => {
    try {
      const [pendingRes, allRes, auditRes, reportsRes] = await Promise.all([
        axios.get(`${API}/users/pending`, authOpts()),
        axios.get(`${API}/users/admin/all`, authOpts()),
        axios.get(`${API}/users/admin/audit-log`, authOpts()),
        axios.get(`${API}/analytics/admin-reports`, authOpts()).catch(() => ({ data: null })),
      ]);
      setPendingUsers(pendingRes.data);
      setAllUsers(allRes.data);
      setAuditAlerts(auditRes.data.alerts || []);
      setReports(reportsRes.data);
    } catch { toast.error('Failed to load data'); }
    finally { setLoading(false); }
  }, [authOpts]);

  const fetchPendingNews = useCallback(async () => {
    setNewsLoading(true);
    try {
      const res = await axios.get(`${API}/news/pending`, authOpts());
      setPendingNews(res.data);
    } catch {}
    finally { setNewsLoading(false); }
  }, [authOpts]);

  const fetchEventRegistrations = useCallback(async () => {
    setRegsLoading(true);
    try {
      const res = await axios.get(`${API}/events/admin/all-registrations?status=${regFilter}`, authOpts());
      setEventRegistrations(res.data);
    } catch { toast.error('Failed to load registrations'); }
    finally { setRegsLoading(false); }
  }, [authOpts, regFilter]);

  const fetchFacts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/marketing/facts`);
      setFacts(res.data.facts || []);
    } catch {}
    finally { setFactsLoading(false); }
  }, []);

  const fetchContent = useCallback(async () => {
    setContentLoading(true);
    try {
      const [discRes, grpRes, svcRes, evtRes] = await Promise.all([
        axios.get(`${API}/discussions/`, authOpts()).catch(() => ({ data: [] })),
        axios.get(`${API}/groups/`, authOpts()).catch(() => ({ data: [] })),
        axios.get(`${API}/marketplace/services`, authOpts()).catch(() => ({ data: [] })),
        axios.get(`${API}/events/?upcoming=false`, authOpts()).catch(() => ({ data: [] })),
      ]);
      setDiscussions(discRes.data);
      setGroups(grpRes.data);
      setServices(svcRes.data);
      setEvents(evtRes.data);
    } catch {}
    finally { setContentLoading(false); }
  }, [authOpts]);

  useEffect(() => { fetchData(); fetchPendingNews(); fetchFacts(); fetchContent(); }, [fetchData, fetchPendingNews, fetchFacts, fetchContent]);
  useEffect(() => { fetchEventRegistrations(); }, [fetchEventRegistrations]);

  // User actions
  const handleApprove = async (userId) => {
    try { await axios.post(`${API}/users/${userId}/approve`, {}, authOpts()); toast.success('User approved!'); fetchData(); } catch { toast.error('Failed'); }
  };
  const handleReject = async (userId) => {
    try { await axios.post(`${API}/users/${userId}/reject`, {}, authOpts()); toast.success('User rejected'); fetchData(); } catch { toast.error('Failed'); }
  };
  const handleActivate = async (userId) => {
    try { await axios.put(`${API}/users/${userId}/activate`, {}, authOpts()); toast.success('User activated'); fetchData(); } catch { toast.error('Failed'); }
  };
  const handleDeactivate = async (userId) => {
    if (!window.confirm('Deactivate this user?')) return;
    try { await axios.put(`${API}/users/${userId}/deactivate`, {}, authOpts()); toast.success('User deactivated'); fetchData(); } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const handleDeleteUser = async (userId, name) => {
    if (!window.confirm(`PERMANENTLY delete user "${name}" and all their data? This cannot be undone.`)) return;
    try { await axios.delete(`${API}/admin/users/${userId}`, authOpts()); toast.success('User deleted permanently'); fetchData(); setManagingUser(null); } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const handleRoleChange = async (userId, newRole) => {
    if (!window.confirm(`Change role to ${newRole}?`)) return;
    try { await axios.put(`${API}/users/${userId}/role`, { role: newRole }, authOpts()); toast.success(`Role changed to ${newRole}`); fetchData(); setManagingUser(null); } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const handleResetPassword = async (userId) => {
    if (!resetPassword || resetPassword.length < 6) { toast.error('Min 6 chars'); return; }
    try { await axios.put(`${API}/users/${userId}/reset-password`, { password: resetPassword, force_change: forceChange }, authOpts()); toast.success('Password reset'); setResetPassword(''); setManagingUser(null); } catch { toast.error('Failed'); }
  };
  const handleVerifyProvider = async (userId) => {
    try { await axios.put(`${API}/users/${userId}/verify`, {}, authOpts()); toast.success('Provider verified!'); fetchData(); setManagingUser(null); } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };
  const handleUnverifyProvider = async (userId) => {
    try { await axios.put(`${API}/users/${userId}/unverify`, {}, authOpts()); toast.success('Verification removed'); fetchData(); setManagingUser(null); } catch { toast.error('Failed'); }
  };

  // News actions
  const handleFetchNews = async () => {
    setFetchingNews(true);
    try { const res = await axios.post(`${API}/news/fetch`, {}, authOpts()); toast.success(res.data.message); fetchPendingNews(); } catch { toast.error('Failed'); }
    finally { setFetchingNews(false); }
  };
  const handleApproveNews = async (id) => {
    try { await axios.post(`${API}/news/${id}/approve`, {}, authOpts()); toast.success('News approved!'); fetchPendingNews(); } catch { toast.error('Failed'); }
  };
  const handleRejectNews = async (id) => {
    try { await axios.post(`${API}/news/${id}/reject`, {}, authOpts()); toast.success('News rejected'); fetchPendingNews(); } catch { toast.error('Failed'); }
  };

  // Registration actions
  const handleApproveRegistration = async (regId) => {
    try { await axios.put(`${API}/events/registrations/${regId}/approve`, {}, authOpts()); toast.success('Approved!'); fetchEventRegistrations(); fetchData(); } catch { toast.error('Failed'); }
  };
  const handleRejectRegistration = async (regId) => {
    try { await axios.put(`${API}/events/registrations/${regId}/reject`, {}, authOpts()); toast.success('Rejected'); fetchEventRegistrations(); fetchData(); } catch { toast.error('Failed'); }
  };
  const handleDeleteRegistration = async (regId) => {
    if (!window.confirm('Delete this registration?')) return;
    try { await axios.delete(`${API}/admin/registrations/${regId}`, authOpts()); toast.success('Registration deleted'); fetchEventRegistrations(); } catch { toast.error('Failed'); }
  };

  // Content delete actions
  const handleDeleteDiscussion = async (id) => {
    if (!window.confirm('Delete this discussion and all replies?')) return;
    try { await axios.delete(`${API}/admin/discussions/${id}`, authOpts()); toast.success('Discussion deleted'); fetchContent(); } catch { toast.error('Failed'); }
  };
  const handleDeleteGroup = async (id) => {
    if (!window.confirm('Delete this group and all its posts?')) return;
    try { await axios.delete(`${API}/admin/groups/${id}`, authOpts()); toast.success('Group deleted'); fetchContent(); } catch { toast.error('Failed'); }
  };
  const handleDeleteService = async (id) => {
    if (!window.confirm('Delete this service?')) return;
    try { await axios.delete(`${API}/admin/services/${id}`, authOpts()); toast.success('Service deleted'); fetchContent(); } catch { toast.error('Failed'); }
  };
  const handleDeleteEvent = async (id) => {
    if (!window.confirm('Delete this event and all registrations?')) return;
    try { await axios.delete(`${API}/admin/events/${id}`, authOpts()); toast.success('Event deleted'); fetchContent(); fetchData(); } catch { toast.error('Failed'); }
  };

  // Marketing facts
  const handleAddFact = () => setFacts(prev => [...prev, { value: '', label: '' }]);
  const handleUpdateFact = (index, field, value) => setFacts(prev => prev.map((f, i) => i === index ? { ...f, [field]: value } : f));
  const handleRemoveFact = (index) => setFacts(prev => prev.filter((_, i) => i !== index));
  const handleSaveFacts = async () => {
    const validFacts = facts.filter(f => f.value.trim() && f.label.trim());
    if (validFacts.length === 0) { toast.error('Add at least one fact'); return; }
    setSavingFacts(true);
    try { await axios.put(`${API}/marketing/facts`, { facts: validFacts }, authOpts()); toast.success('Marketing facts updated!'); } catch { toast.error('Failed'); }
    finally { setSavingFacts(false); }
  };

  const getRoleBadge = (role) => {
    const variants = { admin: 'destructive', lawyer: 'default', broker: 'secondary', user: 'outline' };
    return <Badge variant={variants[role] || 'outline'} className="capitalize">{role}</Badge>;
  };

  const criticalAlerts = auditAlerts.filter(a => a.severity === 'critical');
  const filteredUsers = allUsers.filter(u =>
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.role?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background page-with-bottom-nav" data-testid="admin-page">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 pt-24 pb-12">
        <div className="flex items-center gap-3 mb-6">
          <Shield className="h-7 w-7 sm:h-8 sm:w-8 text-primary" />
          <div>
            <h1 className="font-serif text-2xl sm:text-3xl font-bold">Admin Panel</h1>
            <p className="text-muted-foreground text-sm">Manage users, events, content & marketing</p>
          </div>
        </div>

        {criticalAlerts.length > 0 && (
          <Card className="mb-6 border-destructive/50 bg-destructive/5" data-testid="security-alerts">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                <h3 className="font-semibold text-destructive">Security Alerts ({criticalAlerts.length})</h3>
              </div>
              <div className="space-y-2">
                {criticalAlerts.map((alert, i) => (
                  <div key={i} className="text-sm p-2 rounded bg-destructive/10 flex items-center justify-between">
                    <span>{alert.message}</span>
                    {alert.user_id && <Button size="sm" variant="destructive" onClick={() => handleRoleChange(alert.user_id, 'user')}>Demote</Button>}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {reports && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
            {[
              { icon: Users, label: 'Users', value: allUsers.length, data: reports.users, type: 'users' },
              { icon: Calendar, label: 'Events', value: reports.events?.total || 0, data: reports.events, type: 'events' },
              { icon: BarChart3, label: 'Services', value: reports.services?.total || 0, data: reports.services, type: 'services' },
              { icon: Megaphone, label: 'Posts', value: reports.posts?.this_month || 0, data: reports.posts, type: 'posts' },
            ].map(({ icon: Icon, label, value, data, type }) => (
              <Card key={type} className="card-default" data-testid={`tile-${type}`}>
                <CardContent className="p-3 sm:p-4">
                  <div className="flex items-center gap-2"><Icon className="h-4 w-4 text-primary" /><span className="text-sm font-medium">{label}</span></div>
                  <p className="text-2xl font-serif font-bold mt-1">{value}</p>
                  <TileReport data={data} type={type} />
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <Tabs defaultValue="users" className="space-y-4 sm:space-y-6">
          <TabsList className="flex flex-wrap h-auto gap-1">
            <TabsTrigger value="users" data-testid="tab-users" className="text-xs sm:text-sm"><Users className="h-3.5 w-3.5 mr-1" /> Pending ({pendingUsers.length})</TabsTrigger>
            <TabsTrigger value="manage" data-testid="tab-manage" className="text-xs sm:text-sm"><UserCog className="h-3.5 w-3.5 mr-1" /> All Users</TabsTrigger>
            <TabsTrigger value="events" data-testid="tab-events" className="text-xs sm:text-sm"><Calendar className="h-3.5 w-3.5 mr-1" /> Event Regs</TabsTrigger>
            <TabsTrigger value="content" data-testid="tab-content" className="text-xs sm:text-sm"><FolderOpen className="h-3.5 w-3.5 mr-1" /> Content</TabsTrigger>
            <TabsTrigger value="news" data-testid="tab-news" className="text-xs sm:text-sm"><Newspaper className="h-3.5 w-3.5 mr-1" /> News</TabsTrigger>
            <TabsTrigger value="marketing" data-testid="tab-marketing" className="text-xs sm:text-sm"><Megaphone className="h-3.5 w-3.5 mr-1" /> Marketing</TabsTrigger>
          </TabsList>

          {/* Pending Users */}
          <TabsContent value="users">
            {loading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
            ) : pendingUsers.length === 0 ? (
              <Card className="card-default text-center py-12"><p className="text-muted-foreground">No pending users.</p></Card>
            ) : (
              <div className="space-y-3">
                {pendingUsers.map(u => (
                  <Card key={u.user_id} className="card-default" data-testid={`pending-${u.user_id}`}>
                    <CardContent className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-9 w-9 sm:h-10 sm:w-10"><AvatarImage src={u.picture} /><AvatarFallback>{u.name?.charAt(0)?.toUpperCase()}</AvatarFallback></Avatar>
                        <div>
                          <p className="font-medium text-sm sm:text-base">{u.name}</p>
                          <p className="text-xs sm:text-sm text-muted-foreground">{u.email}</p>
                        </div>
                        {getRoleBadge(u.role)}
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" onClick={() => handleApprove(u.user_id)} data-testid={`approve-${u.user_id}`}><Check className="h-4 w-4 mr-1" /> Approve</Button>
                        <Button size="sm" variant="destructive" onClick={() => handleReject(u.user_id)} data-testid={`reject-${u.user_id}`}><X className="h-4 w-4 mr-1" /> Reject</Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* All Users Management */}
          <TabsContent value="manage">
            <div className="mb-4">
              <Input placeholder="Search users..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="input-default max-w-md" data-testid="user-search-input" />
            </div>
            <div className="space-y-3">
              {filteredUsers.map(u => (
                <Card key={u.user_id} className={`card-default ${!u.is_active ? 'opacity-60' : ''}`} data-testid={`user-row-${u.user_id}`}>
                  <CardContent className="p-3 sm:p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <Avatar className="h-9 w-9 sm:h-10 sm:w-10 flex-shrink-0"><AvatarImage src={u.picture} /><AvatarFallback>{u.name?.charAt(0)?.toUpperCase()}</AvatarFallback></Avatar>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-sm sm:text-base truncate">{u.name}</p>
                            {getRoleBadge(u.role)}
                            {!u.is_active && <Badge variant="destructive">Inactive</Badge>}
                            {u.is_verified && <Badge className="bg-blue-500/10 text-blue-500 gap-1"><BadgeCheck className="h-3 w-3" />Verified</Badge>}
                          </div>
                          <p className="text-xs sm:text-sm text-muted-foreground truncate">{u.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {u.user_id !== user?.user_id ? (
                          <>
                            {u.is_active ? (
                              <Button size="sm" variant="outline" className="text-destructive text-xs" onClick={() => handleDeactivate(u.user_id)} data-testid={`deactivate-${u.user_id}`}>
                                <PowerOff className="h-3 w-3 mr-1" /> Deactivate
                              </Button>
                            ) : (
                              <Button size="sm" variant="outline" className="text-xs" onClick={() => handleActivate(u.user_id)} data-testid={`activate-${u.user_id}`}>
                                <Power className="h-3 w-3 mr-1" /> Activate
                              </Button>
                            )}
                            <Button size="sm" variant="outline" className="text-xs" onClick={() => { setManagingUser(u); setResetPassword(''); }} data-testid={`manage-${u.user_id}`}>
                              <Key className="h-3 w-3 mr-1" /> Manage
                            </Button>
                            <Button size="sm" variant="ghost" className="text-destructive text-xs" onClick={() => handleDeleteUser(u.user_id, u.name)} data-testid={`delete-user-${u.user_id}`}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </>
                        ) : (
                          <Badge className="bg-primary/10 text-primary">You</Badge>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Event Registrations */}
          <TabsContent value="events">
            <div className="flex items-center gap-3 mb-4">
              <Select value={regFilter} onValueChange={setRegFilter}>
                <SelectTrigger className="w-40 input-default" data-testid="reg-filter-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="waitlisted">Waitlisted</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={fetchEventRegistrations}><RefreshCw className="h-4 w-4" /></Button>
            </div>
            {regsLoading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
            ) : eventRegistrations.length === 0 ? (
              <Card className="card-default text-center py-12"><p className="text-muted-foreground">No {regFilter} registrations.</p></Card>
            ) : (
              <div className="space-y-3">
                {eventRegistrations.map(reg => (
                  <Card key={reg.registration_id} className="card-default" data-testid={`reg-${reg.registration_id}`}>
                    <CardContent className="p-3 sm:p-4">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div>
                          <p className="font-medium text-sm sm:text-base">{reg.user_name}</p>
                          <p className="text-xs text-muted-foreground">{reg.user_email}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="secondary" className="text-xs"><Calendar className="h-3 w-3 mr-1" />{reg.event_title || 'Event'}</Badge>
                            {reg.event_date && <span className="text-xs text-muted-foreground">{new Date(reg.event_date).toLocaleDateString()}</span>}
                            {reg.is_waitlisted && <Badge className="bg-amber-500/10 text-amber-500 text-xs">Waitlisted</Badge>}
                          </div>
                        </div>
                        <div className="flex gap-2 items-center">
                          {reg.status === 'pending' && (
                            <>
                              <Button size="sm" onClick={() => handleApproveRegistration(reg.registration_id)} data-testid={`approve-reg-${reg.registration_id}`}><Check className="h-4 w-4 mr-1" /> Approve</Button>
                              <Button size="sm" variant="destructive" onClick={() => handleRejectRegistration(reg.registration_id)} data-testid={`reject-reg-${reg.registration_id}`}><X className="h-4 w-4 mr-1" /> Reject</Button>
                            </>
                          )}
                          {reg.status === 'approved' && <Badge className="bg-green-500/10 text-green-500">Approved</Badge>}
                          {reg.status === 'rejected' && <Badge variant="destructive">Rejected</Badge>}
                          {reg.status === 'waitlisted' && <Badge className="bg-amber-500/10 text-amber-500">Waitlisted</Badge>}
                          <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDeleteRegistration(reg.registration_id)} data-testid={`delete-reg-${reg.registration_id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Content Management */}
          <TabsContent value="content">
            <div className="flex gap-2 mb-4 flex-wrap">
              {[
                { key: 'discussions', label: 'Discussions', icon: MessageSquare, count: discussions.length },
                { key: 'groups', label: 'Groups', icon: Users, count: groups.length },
                { key: 'services', label: 'Services', icon: ShoppingBag, count: services.length },
                { key: 'events', label: 'Events', icon: Calendar, count: events.length },
              ].map(({ key, label, icon: Icon, count }) => (
                <Button key={key} variant={contentTab === key ? 'default' : 'outline'} size="sm" onClick={() => setContentTab(key)} data-testid={`content-tab-${key}`}>
                  <Icon className="h-3.5 w-3.5 mr-1" /> {label} ({count})
                </Button>
              ))}
              <Button variant="outline" size="sm" onClick={fetchContent}><RefreshCw className="h-4 w-4" /></Button>
            </div>

            {contentLoading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
            ) : (
              <div className="space-y-3">
                {contentTab === 'discussions' && (discussions.length === 0 ? (
                  <Card className="card-default text-center py-12"><p className="text-muted-foreground">No discussions.</p></Card>
                ) : discussions.map(d => (
                  <Card key={d.discussion_id} className="card-default" data-testid={`disc-${d.discussion_id}`}>
                    <CardContent className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-medium text-sm sm:text-base truncate">{d.title}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-muted-foreground">by {d.author_name}</span>
                          <Badge variant="outline" className="text-xs">{d.category}</Badge>
                          <span className="text-xs text-muted-foreground">{d.reply_count || 0} replies</span>
                        </div>
                      </div>
                      <Button size="sm" variant="ghost" className="text-destructive flex-shrink-0" onClick={() => handleDeleteDiscussion(d.discussion_id)} data-testid={`delete-disc-${d.discussion_id}`}>
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                      </Button>
                    </CardContent>
                  </Card>
                )))}

                {contentTab === 'groups' && (groups.length === 0 ? (
                  <Card className="card-default text-center py-12"><p className="text-muted-foreground">No groups.</p></Card>
                ) : groups.map(g => (
                  <Card key={g.group_id} className="card-default" data-testid={`group-${g.group_id}`}>
                    <CardContent className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div>
                        <p className="font-medium text-sm sm:text-base">{g.name}</p>
                        <p className="text-xs text-muted-foreground">{g.member_count || 0} members {g.is_private ? '(Private)' : ''}</p>
                      </div>
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDeleteGroup(g.group_id)} data-testid={`delete-group-${g.group_id}`}>
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                      </Button>
                    </CardContent>
                  </Card>
                )))}

                {contentTab === 'services' && (services.length === 0 ? (
                  <Card className="card-default text-center py-12"><p className="text-muted-foreground">No services.</p></Card>
                ) : services.map(s => (
                  <Card key={s.service_id} className="card-default" data-testid={`svc-${s.service_id}`}>
                    <CardContent className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div>
                        <p className="font-medium text-sm sm:text-base">{s.title}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-muted-foreground">by {s.provider_name}</span>
                          <Badge variant="outline" className="text-xs">${s.price}</Badge>
                          <Badge variant="secondary" className="text-xs">{s.category}</Badge>
                        </div>
                      </div>
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDeleteService(s.service_id)} data-testid={`delete-svc-${s.service_id}`}>
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                      </Button>
                    </CardContent>
                  </Card>
                )))}

                {contentTab === 'events' && (events.length === 0 ? (
                  <Card className="card-default text-center py-12"><p className="text-muted-foreground">No events.</p></Card>
                ) : events.map(e => (
                  <Card key={e.event_id} className="card-default" data-testid={`event-${e.event_id}`}>
                    <CardContent className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div>
                        <p className="font-medium text-sm sm:text-base">{e.title}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-xs text-muted-foreground">{new Date(e.start_date).toLocaleDateString()}</span>
                          <span className="text-xs text-muted-foreground">{e.attendee_count || 0} attendees</span>
                          {e.max_capacity && <Badge variant="outline" className="text-xs">Cap: {e.max_capacity}</Badge>}
                        </div>
                      </div>
                      <Button size="sm" variant="ghost" className="text-destructive" onClick={() => handleDeleteEvent(e.event_id)} data-testid={`delete-event-${e.event_id}`}>
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                      </Button>
                    </CardContent>
                  </Card>
                )))}
              </div>
            )}
          </TabsContent>

          {/* News Tab */}
          <TabsContent value="news">
            <div className="flex justify-end mb-4">
              <Button onClick={handleFetchNews} disabled={fetchingNews} variant="outline" data-testid="fetch-news-btn">
                <RefreshCw className={`h-4 w-4 mr-2 ${fetchingNews ? 'animate-spin' : ''}`} /> Fetch News
              </Button>
            </div>
            {newsLoading ? (
              <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
            ) : pendingNews.length === 0 ? (
              <Card className="card-default text-center py-12"><p className="text-muted-foreground">No pending news. Click "Fetch News" to scan.</p></Card>
            ) : (
              <div className="space-y-3">
                {pendingNews.map(news => (
                  <Card key={news.news_id} className="card-default" data-testid={`news-${news.news_id}`}>
                    <CardContent className="p-3 sm:p-4">
                      <h3 className="font-semibold mb-1 text-sm sm:text-base">{news.title}</h3>
                      <p className="text-xs sm:text-sm text-muted-foreground mb-2">{news.summary}</p>
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <a href={news.link} target="_blank" rel="noopener noreferrer" className="text-xs text-primary flex items-center gap-1">{news.source} <ExternalLink className="h-3 w-3" /></a>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handleApproveNews(news.news_id)}><Check className="h-4 w-4 mr-1" /> Approve</Button>
                          <Button size="sm" variant="outline" onClick={() => handleRejectNews(news.news_id)}><X className="h-4 w-4 mr-1" /> Reject</Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Marketing Facts */}
          <TabsContent value="marketing">
            <Card className="card-default">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg"><Megaphone className="h-5 w-5 text-primary" /> Landing Page Stats</CardTitle>
                <p className="text-sm text-muted-foreground">These stats are displayed on the front page.</p>
              </CardHeader>
              <CardContent>
                {factsLoading ? (
                  <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
                ) : (
                  <div className="space-y-4">
                    {facts.map((fact, index) => (
                      <div key={index} className="flex items-center gap-3" data-testid={`fact-row-${index}`}>
                        <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <Input placeholder="Value (e.g., 10K+)" value={fact.value} onChange={e => handleUpdateFact(index, 'value', e.target.value)} className="input-default" data-testid={`fact-value-${index}`} />
                          <Input placeholder="Label" value={fact.label} onChange={e => handleUpdateFact(index, 'label', e.target.value)} className="input-default" data-testid={`fact-label-${index}`} />
                        </div>
                        <Button size="icon" variant="ghost" onClick={() => handleRemoveFact(index)} className="text-destructive flex-shrink-0" data-testid={`fact-remove-${index}`}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    ))}
                    <div className="flex gap-3 pt-2">
                      <Button variant="outline" onClick={handleAddFact} data-testid="add-fact-btn"><Plus className="h-4 w-4 mr-2" /> Add Fact</Button>
                      <Button onClick={handleSaveFacts} disabled={savingFacts} className="btn-primary" data-testid="save-facts-btn">
                        {savingFacts ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Save Changes
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* User Management Dialog */}
        <Dialog open={!!managingUser} onOpenChange={() => setManagingUser(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="font-serif text-xl flex items-center gap-2"><UserCog className="h-5 w-5" /> Manage User</DialogTitle></DialogHeader>
            {managingUser && (
              <div className="space-y-6 mt-4">
                <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                  <Avatar className="h-10 w-10"><AvatarImage src={managingUser.picture} /><AvatarFallback>{managingUser.name?.charAt(0)?.toUpperCase()}</AvatarFallback></Avatar>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{managingUser.name}</p>
                      {managingUser.is_verified && <BadgeCheck className="h-4 w-4 text-blue-500" />}
                    </div>
                    <p className="text-sm text-muted-foreground">{managingUser.email}</p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2"><Shield className="h-4 w-4" /> Change Role</Label>
                  <Select value={managingUser.role} onValueChange={v => handleRoleChange(managingUser.user_id, v)}>
                    <SelectTrigger className="input-default"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="lawyer">Lawyer</SelectItem>
                      <SelectItem value="broker">Broker</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Verification for providers */}
                {(managingUser.role === 'lawyer' || managingUser.role === 'broker') && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2"><BadgeCheck className="h-4 w-4" /> Provider Verification</Label>
                    {managingUser.is_verified ? (
                      <Button variant="outline" className="w-full text-destructive" onClick={() => handleUnverifyProvider(managingUser.user_id)} data-testid="unverify-btn">
                        <X className="h-4 w-4 mr-2" /> Remove Verification
                      </Button>
                    ) : (
                      <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={() => handleVerifyProvider(managingUser.user_id)} data-testid="verify-btn">
                        <BadgeCheck className="h-4 w-4 mr-2" /> Verify Provider
                      </Button>
                    )}
                  </div>
                )}

                <div className="space-y-3">
                  <Label className="flex items-center gap-2"><Lock className="h-4 w-4" /> Reset Password</Label>
                  <Input type="password" placeholder="New password (min 6 chars)" value={resetPassword} onChange={e => setResetPassword(e.target.value)} className="input-default" data-testid="reset-password-input" />
                  <div className="flex items-center gap-2">
                    <Switch id="force-change" checked={forceChange} onCheckedChange={setForceChange} />
                    <Label htmlFor="force-change" className="text-sm">Force change on next login</Label>
                  </div>
                  <Button onClick={() => handleResetPassword(managingUser.user_id)} disabled={!resetPassword || resetPassword.length < 6} className="w-full" variant="outline" data-testid="reset-password-btn">
                    <Key className="h-4 w-4 mr-2" /> Reset Password
                  </Button>
                </div>

                <Button variant="destructive" className="w-full" onClick={() => handleDeleteUser(managingUser.user_id, managingUser.name)} data-testid="delete-user-dialog-btn">
                  <Trash2 className="h-4 w-4 mr-2" /> Delete User Permanently
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminPage;
